import type { IQuoteRequest, IQuoteResponse } from "../services/api";

export interface QuoteRefreshRoute {
  sourceCountry: string;
  destinationCountry: string;
  /** Fallback source currency when the quote snapshot omits it. */
  sourceCurrency: string;
}

/**
 * Rebuilds a binding quote request after `QUOTE_EXPIRED`, preserving the
 * amount anchor the user confirmed. Receive-anchored quotes re-pin
 * `receiveAmount` + `destinationCurrency` (not `sendAmount`).
 *
 * `currency` is always the source leg; `destinationCurrency` is always sent
 * so receive anchors satisfy `DESTINATION_CURRENCY_REQUIRED`.
 */
export function buildQuoteRefreshRequest(
  quote: Pick<
    IQuoteResponse,
    | "sendAmount"
    | "receiveAmount"
    | "sourceCurrency"
    | "destinationCurrency"
    | "fundingMethod"
    | "payoutMethod"
    | "capabilityKey"
    | "anchor"
    | "cardId"
  >,
  route: QuoteRefreshRoute
): IQuoteRequest {
  const isReceiveAnchored = quote.anchor === "receive";
  const sourceCurrency = quote.sourceCurrency || route.sourceCurrency;
  const request: IQuoteRequest = {
    sourceCountry: route.sourceCountry,
    destinationCountry: route.destinationCountry,
    fundingMethod: quote.fundingMethod,
    payoutMethod: quote.payoutMethod,
    capabilityKey: quote.capabilityKey ?? "",
    amount: isReceiveAnchored ? quote.receiveAmount : quote.sendAmount,
    currency: sourceCurrency,
    destinationCurrency: quote.destinationCurrency,
    ...(quote.anchor ? { anchor: quote.anchor } : {}),
  };
  if (quote.cardId) {
    request.cardId = quote.cardId;
  }
  return request;
}
